import React from 'react';
import { useTranslation } from 'react-i18next';
import { Group, Path, Rect, Text } from 'react-konva';
import type { Node } from '../../../core/models/scene';
import { useDiagramStore } from '../../../stores/diagramStore';
import {
  TAG_BADGE_GLYPH_GAP,
  TAG_BADGE_GLYPH_WIDTH,
  TAG_BADGE_HEIGHT,
  TAG_BADGE_CHAR_PX,
  TAG_BADGE_PAD_X,
  layoutTagBadgeChips,
  resolveTagBadgeLayout,
  resolveTagBadgeTags,
} from '../../../lib/diagram/tagBadgeLayout';

// Tag chips overlaid on flowchart / BPMN process shapes.
//
// Mirrors the `GrcBadges` pattern next to which it mounts in NodesRenderer:
// a sibling Konva <Group> with `listening={false}` so it can't intercept
// clicks on the shape, positioned in absolute canvas coordinates from
// `node.x` / `node.y` / `node.h`.
//
// Tags are stored on the node's attrs by ProcessPropertyPanel (field id
// `'tags'` from operations.pack.ts). The legacy inferFieldPacks lookup in
// ProcessBasicPropertiesTool reads from top-level / props / attrs, so we
// fall back through the same chain to handle every shape shape variant.
//
// Coloring stays neutral on purpose: tag-driven shape coloring is a job for
// the lens preset system (viewRulesStore), not for these badges. Keeping
// the two axes separate ("tag is present" vs. "shape coloured by tag")
// avoids a second, conflicting color system on the canvas. See
// docs/core/architecture-shapes-connectors.md ("Visual props and domain
// data separate") and the discussion in
// plans/canvas-bottom-toolbar-repitch.md.

interface ProcessTagBadgesProps {
  node: Node;
  visible?: boolean;
  reserveDependencyRow?: boolean;
}

// Visible chip count before collapsing into the `+N` overflow chip. Mirrors
// the truncation pattern in PersonNode.renderHealthEmojiBadges (3 + extra).
function getAttrs(node: Node): Record<string, unknown> {
  return (node as { attrs?: Record<string, unknown>; props?: { attrs?: Record<string, unknown> } }).attrs
    ?? (node as { props?: { attrs?: Record<string, unknown> } }).props?.attrs
    ?? {};
}

export const ProcessTagBadges: React.FC<ProcessTagBadgesProps> = ({
  node,
  visible = true,
  reserveDependencyRow = true,
}) => {
  const { t } = useTranslation('process');
  const stageScale = useDiagramStore((s) => s.stage?.scale ?? 1);
  const attrs = getAttrs(node);

  // Skip pool tables and non-assurable node types — match GrcBadges
  // gating exactly so the two overlays appear and disappear under the
  // same conditions. The provider-prefixed types cover network/cloud
  // resources that ride the shape-manifest path.
  if (!visible) return null;
  if (attrs.__isPoolTable) return null;
  const type = (node as { type?: string }).type;
  const isAllowedProcessType =
    type === 'flowchart-shape' ||
    type === 'bpmn-shape' ||
    type === 'process' ||
    type === 'decision';
  const isCloudResourceType =
    typeof type === 'string' &&
    (type.startsWith('aws_') ||
      type.startsWith('basic_') ||
      type.startsWith('azure_') ||
      type.startsWith('gcp_'));
  if (!isAllowedProcessType && !isCloudResourceType) {
    return null;
  }

  const tags = resolveTagBadgeTags(node);
  if (tags.length === 0) return null;

  const x = (node.x ?? 0) as number;
  const y = (node.y ?? 0) as number;
  const h = ((node as { h?: number; height?: number }).h ?? (node as { height?: number }).height ?? 80) as number;

  // Stack below the GRC dependency-pill row if present; otherwise sit
  // directly under the shape. Keeps the canvas calm — at most one row of
  // overlay decoration at any given y when one decoration is in play.
  const layout = resolveTagBadgeLayout(node, { reserveDependencyRow });
  if (!layout) return null;
  const baseY = y + h + layout.offsetY;

  // Below ~0.5 stage scale individual chips are unreadable. Collapse to a
  // single count chip so users still see the *presence* of tags without
  // visual noise. Mirrors the overflow-chip pattern in
  // PersonNode.renderHealthEmojiBadges.
  if (stageScale < 0.5) {
    const text: string = t('tagBadges.tagCount', { count: tags.length });
    const width = text.length * TAG_BADGE_CHAR_PX + TAG_BADGE_PAD_X * 2;
    return (
      <Group listening={false} x={x} y={baseY}>
        <Rect
          width={width}
          height={TAG_BADGE_HEIGHT}
          fill="#f1f5f9"
          stroke="#cbd5e1"
          strokeWidth={1}
          cornerRadius={TAG_BADGE_HEIGHT / 2}
          listening={false}
          perfectDrawEnabled={false}
        />
        <Text
          x={TAG_BADGE_PAD_X}
          y={3}
          text={text}
          fontSize={10}
          fill="#475569"
          listening={false}
        />
      </Group>
    );
  }

  const placements = layoutTagBadgeChips(tags);

  return (
    <Group listening={false} x={x} y={baseY}>
      {placements.map((p) => {
        const labelX = p.icon ? TAG_BADGE_PAD_X + TAG_BADGE_GLYPH_WIDTH + TAG_BADGE_GLYPH_GAP : TAG_BADGE_PAD_X;
        return (
          <Group key={`${p.variant}-${p.text}-${p.x}`} x={p.x}>
            <Rect
              width={p.width}
              height={TAG_BADGE_HEIGHT}
              fill={p.variant === 'overflow' ? '#e2e8f0' : '#f1f5f9'}
              stroke={p.variant === 'overflow' ? '#94a3b8' : '#cbd5e1'}
              strokeWidth={1}
              cornerRadius={TAG_BADGE_HEIGHT / 2}
              listening={false}
              perfectDrawEnabled={false}
            />
            {p.icon && (
              <Group
                x={TAG_BADGE_PAD_X}
                y={2}
                scaleX={0.5}
                scaleY={0.5}
                listening={false}
              >
                {p.icon.layers.map((layer, index) => (
                  <Path
                    key={`${p.text}-icon-${index}`}
                    data={layer.path}
                    fill={layer.fill}
                    fillEnabled={Boolean(layer.fill)}
                    fillRule={layer.fillRule}
                    stroke={layer.stroke}
                    strokeEnabled={Boolean(layer.stroke)}
                    strokeWidth={layer.strokeWidth ?? 1}
                    lineCap="round"
                    lineJoin="round"
                    listening={false}
                    perfectDrawEnabled={false}
                  />
                ))}
              </Group>
            )}
            <Text
              x={labelX}
              y={3}
              text={p.text}
              fontSize={10}
              fill="#475569"
              fontStyle={p.variant === 'overflow' ? 'bold' : 'normal'}
              listening={false}
            />
          </Group>
        );
      })}
    </Group>
  );
};
